/**
 * SearchBar - Premium search bar matching v0 discover screen
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { hapticFeedback } from '../lib/telegram';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  onCategoryQuickFilter?: (category: string) => void;
}

export function SearchBar({
  value,
  onChange,
  placeholder = 'Search AI agents...',
  debounceMs = 300,
}: SearchBarProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = useCallback(
    (newValue: string) => {
      setLocalValue(newValue);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onChange(newValue), debounceMs);
    },
    [onChange, debounceMs]
  );

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleClear = useCallback(() => {
    hapticFeedback('light');
    setLocalValue('');
    onChange('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [onChange]);

  return (
    <div className="space-y-2">
      <div className={`relative transition-all duration-200 ${isFocused ? 'glass rounded-xl' : ''}`}>
        {/* Search icon */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg className="w-5 h-5 text-[#a0a0a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        <input
          type="text"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 h-12 rounded-xl 
            bg-[#242424] border border-white/5
            text-white 
            placeholder-[#a0a0a0]
            focus:border-[#0098EA]/50
            focus:outline-none
            transition-all duration-200"
        />

        {localValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Clear search"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
